import { useState } from 'react';
import { sortRows } from '../../utils/tableSort';

// Click a column heading to sort by it, click again to reverse it. First used on the
// Company card spend tables; the shape is the Inventory page's SortHeader, shared.
//
// A column is sorted by a value function, not by what the cell shows: the cell for a
// date reads "3 Oct" and the cell for money reads "£1,204.00", neither of which sorts.
// The ordering itself is in utils/tableSort.js, so the tests can run it.

// columns: { key: row => value }. initial: [key, dir], or null for the order it arrived in.
// A first click sorts ascending unless firstDir names the column: the card spend tables
// pass 'desc' for dates and money, since the newest and the largest are usually what
// somebody is after.
export function useSort(rows, columns, initial = null, firstDir = {}) {
  const [sort, setSort] = useState(initial ? { key: initial[0], dir: initial[1] } : null);
  const toggle = (key) => setSort(cur => (cur?.key === key
    ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: firstDir[key] || 'asc' }));
  // Worked out on every render rather than memoised: a column can read something that
  // loads after the rows (the ledger list behind a nominal code), and these tables are a
  // few hundred rows at most. Payments, which is capped at 500, sorts on the server.
  const sorted = sort && columns[sort.key] ? sortRows(rows || [], columns[sort.key], sort.dir) : rows || [];
  return { sorted, sort, toggle };
}

// A heading that sorts. Pass `sort` and `onSort` from useSort (or from a server-sorted
// list that keeps the same { key, dir } shape). `className` carries the table's own
// heading style so each table keeps its look.
export function SortTh({ col, label, sort, onSort, className = '', right = false, title }) {
  const active = sort?.key === col;
  return (
    <th
      className={`${className} ${right ? 'text-right' : 'text-left'} cursor-pointer select-none whitespace-nowrap hover:text-gray-700`}
      onClick={() => onSort(col)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title={title || `Sort by ${String(label).toLowerCase()}`}
    >
      <span className={active ? 'text-navy' : ''}>{label}</span>
      <span className={`ml-0.5 ${active ? 'text-navy' : 'text-gray-300'}`} aria-hidden="true">
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
}
